import { MODE } from "../Alert/Filter/CreateFilterDateForm";
import { Operator } from "../constants";
import { getCurrentLocale } from "../Locale";
import {
  AlertCatalog,
  AlertDimension,
  AlertExportRequest,
  AlertListRequest,
  AlertOrdering,
  buildAlertCountRequest,
  GranularityType,
  kpiCode,
} from "./AlertListFilterPayload";
import { checkErrors, DimensionValue, getDefaultOptions } from "./Category";
import { listKpiCatalog } from "./Kpi";
import { Module } from "./Module";
import { getTask, TaskStatus, watchTask } from "./Task";
import { ErrorResponse } from "./utils";
import { PriceUpdateStatus } from "./Price";
import { FilterType } from "../Alert/Filter/FilterType";

export interface AlertEntity {
  id: number;
  code: string | null;
  name: string | null;
}

interface AlertBase {
  date: string;
  property: AlertEntity;
  event: string | null;
  cockpit_url: string | null;
  /** KPI code -> raw value. */
  kpis: Record<string, number | null>;
}

export type AlertProperty = AlertBase;
export type AlertUhTypeNandc = AlertBase & { uh_type_nandc: AlertEntity };
export type AlertUhCat = AlertBase & { uh_cat: AlertEntity };
export type Alert = AlertProperty | AlertUhTypeNandc | AlertUhCat;

/** Unique within a list: an alert is a stay date of one entity. */
export const alertKey = (alert: Alert): string =>
  [
    alert.property.id,
    "uh_type_nandc" in alert ? alert.uh_type_nandc.id : "",
    "uh_cat" in alert ? alert.uh_cat.id : "",
    alert.date,
  ].join("_");

export interface SimpleGenericPaginatedResponse<T> {
  count: number;
  results: Array<T>;
}

// export interface DateOptions {
//   type: MODE;
//   start?: string;
//   end?: string;
// }
export type DateOptions = {
  __typename: "date";
} & (
  | {
      type: Extract<MODE, MODE.WEEK | MODE.MONTH | MODE.YEAR>;
      start: never;
      end: never;
    }
  | {
      type: Extract<MODE, MODE.CUSTOM>;
      start: string;
      end: string;
    }
  | {
      type: Extract<MODE, MODE.LEAD_TIME>;
      days: number;
      start?: never;
      end?: never;
    }
);

export interface DimensionOptions {
  __typename: "dimension";
  values: Array<DimensionValue>;
}

export interface StatusOptions {
  value: PriceUpdateStatus;
}

export interface DateRangeOptions {
  values: string[];
}

export interface PickupOptions {
  date_observation: string | null;
  date_reservation_start: string | null;
  date_reservation_end: string | null;
}

/** Date the pickup tabs measure against: everything booked between it and
 *  the day before the observation date. */
export interface ComparisonOptions {
  comparison_date: string | null;
}

export interface KpiOptions {
  __typename: "kpi";
  // id of the category
  granularity?: GranularityType;
  parent: number;
  operator: Operator;
  // target or kpi ?
  // target: number;
  kpi: number;
  value: number;
}

export type FilterOptions =
  | DateOptions
  | DimensionOptions
  | KpiOptions
  | StatusOptions
  | PickupOptions
  | ComparisonOptions
  | DateRangeOptions;

export interface Filter {
  id?: number;
  target: number | null;
  type: FilterType;
  options: FilterOptions;
}

export interface FilterGroup {
  id: number;
  favorite: boolean;
  name: string;
  readonly?: boolean;
  // TODO: add type
  type: "kpi" | "perimeter";
  filters: Filter[];
}

// This is a pain
// export type FilterGroup = {
//   id: number;
//   favorite: boolean;
//   name: string;
// } & ({
//   type: "perimeter"
//   filters: FilterDimension[];
// } | {
//   type: "kpi"
//   filters: FilterKpi[];
// });

export interface AlertCountByProperty {
  property: number;
  count: number;
}

// Active alerts counted per property over a stay-date range (user-scoped
// server-side). Consumed by the reporting map (alert metric + tooltip).
export const fetchAlertCountsByProperty = async (
  startDate: string,
  endDate: string
): Promise<AlertCountByProperty[]> => {
  const url = new URL("/api/alerts/counts_by_property/", window.origin);
  url.searchParams.set("start_date", startDate);
  url.searchParams.set("end_date", endDate);
  const response = await fetch(url.toString(), getDefaultOptions());
  checkErrors(response);
  return response.json();
};

const listAlertDimensions = async (): Promise<Map<number, AlertDimension>> => {
  const response = await fetch(
    "/api/dimensions/?page_size=1000",
    getDefaultOptions()
  );
  checkErrors(response);
  const json: SimpleGenericPaginatedResponse<{
    id: number;
    name: string | null;
    dimension: { granularity: number };
  }> = await response.json();
  return new Map(
    json.results.flatMap(({ id, name, dimension }) =>
      name ? [[id, { name, granularity: dimension.granularity }] as const] : []
    )
  );
};

// One catalog per customer: KPI ids can differ between tenants.
const alertCatalogs = new Map<string, Promise<AlertCatalog>>();

export const loadAlertCatalog = (): Promise<AlertCatalog> => {
  const customer = localStorage.getItem("selectedCustomer") ?? "";
  const cached = alertCatalogs.get(customer);
  if (cached) return cached;
  const loading = Promise.all([listKpiCatalog(), listAlertDimensions()]).then(
    ([catalog, dimensions]) => ({
      kpiCodes: new Map(catalog.kpis.map((kpi) => [kpi.id, kpi.name])),
      dimensions,
    })
  );
  loading.catch(() => alertCatalogs.delete(customer));
  alertCatalogs.set(customer, loading);
  return loading;
};

export const postAlertList = async (
  request: AlertListRequest
): Promise<SimpleGenericPaginatedResponse<Alert>> => {
  const response = await fetch(
    "/api/alerts/list/",
    getDefaultOptions({ method: "POST", body: JSON.stringify(request) })
  );
  checkErrors(response);
  return response.json();
};

export const listAlerts = async (
  filters: { dimensions: Array<Filter>; kpis: Array<Filter> },
  params: {
    granularity: GranularityType;
    kpiIds: number[];
    ordering: AlertOrdering;
    page: number;
    pageSize: number;
  }
) => {
  const catalog = await loadAlertCatalog();
  const request: AlertListRequest = {
    ...buildAlertCountRequest(filters, catalog, params.granularity),
    kpis: params.kpiIds.map((id) => kpiCode(catalog, id)),
    ordering: params.ordering,
    page: params.page,
    page_size: params.pageSize,
  };
  return { request, ...(await postAlertList(request)) };
};

export const getId = (filters: Filter[]) =>
  filters.reduce((prev, current) => Math.max(prev, current.id || -1), 0) + 1;

const normalizeFilters = (filters: Filter[]) => {
  let id = getId(filters);
  return filters.map((filter) => {
    return { ...filter, id: filter.id || id++ };
  });
};

export const baseListPresets = async (
  type: string,
  moduleSlug: string = Module.ALERT
) => {
  const url = new URL("/api/presets/", window.origin);
  const params = { type, module__slug: moduleSlug };

  url.search = new URLSearchParams(params).toString();

  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  return response.json();
};

export const listGroups = async (
  type: string,
  moduleSlug: string = Module.ALERT
) => {
  const json = await baseListPresets(type, moduleSlug);

  return {
    count: json.count,
    results: json.results.map((group: any) => ({
      ...group,
      filters: normalizeFilters(group.filters),
    })),
  };
};

export const createGroup = async (group: Omit<FilterGroup, "id">) => {
  const url = new URL(`/api/presets/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(group),
    })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }
  return response.json();
};

export const updateGroup = async (group: FilterGroup) => {
  const url = new URL(`/api/presets/${group.id}/`, window.origin);
  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "PUT",
      body: JSON.stringify(group),
    })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }

  return response.json();
};

export const deleteGroup = async (groupId: number) => {
  const url = new URL(`/api/presets/${groupId}/`, window.origin);
  const response = await fetch(
    url.toString(),
    getDefaultOptions({ method: "DELETE" })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }
  return groupId;
};

/**
 * Get the favorite group.
 */
export const getFavoriteGroup = (groups: FilterGroup[]): null | FilterGroup =>
  groups.find((group: FilterGroup) => group.favorite) || null;

export const setFavoriteGroup = async (groupId: number, favorite: boolean) => {
  const url = new URL(`/api/presets/${groupId}/favorite/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "PUT",
      body: JSON.stringify({ favorite }),
    })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }

  return response.json();
};

export interface ExportParams {
  filters: { dimensions: Array<Filter>; kpis: Array<Filter> };
  granularity: GranularityType;
  kpiIds: number[];
  ordering: AlertOrdering;
  /** Column headers as displayed in the app, keyed by field or KPI code. */
  columnLabels?: Record<string, string>;
}

export const exportAlerts = async (params: ExportParams): Promise<any> => {
  const url = new URL(`/api/alerts/export/`, window.origin);

  const catalog = await loadAlertCatalog();
  const request: AlertExportRequest = {
    ...buildAlertCountRequest(params.filters, catalog, params.granularity),
    kpis: params.kpiIds.map((id) => kpiCode(catalog, id)),
    ordering: params.ordering,
    column_labels: params.columnLabels,
  };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({ method: "POST", body: JSON.stringify(request) })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }

  return response.json();
};

export const exportOptiReport = async (): Promise<any> => {
  const url = new URL(`/api/reports/optimizations-reports/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
    })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }
  // Using href is the easiest way to download a file
  // using content-disposition filename bypassing
  // CORS and redirections problems ans still downloading
  // from AWS servers
  return response.json().then((data) => {
    window.location.href = data["target"];
  });
};

export const exportPriceReport = async (
  onStarted?: (estimatedSeconds: number | null) => void
): Promise<void> => {
  const url = new URL(`/api/reports/prices-reports/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      headers: {
        "Accept-Language": getCurrentLocale(),
      },
    })
  );

  try {
    checkErrors(response);
  } catch (error) {
    // Catching here because of the needed await
    const json: ErrorResponse = await response.json();
    throw new Error(json.error);
  }

  // The report is generated asynchronously on a worker (can take minutes for
  // big clients). We poll the task and download the S3 file once it is ready.
  const task = await response.json();
  // Surface the ETA (median of recent runs) as soon as the task is queued.
  onStarted?.(task.estimated_seconds ?? null);
  return new Promise<void>((resolve, reject) => {
    watchTask({
      task: task.id,
      getTaskFn: getTask,
      onSuccess: (updatedTask: any) => {
        if (
          updatedTask.status === TaskStatus.FINISHED &&
          updatedTask.parameters?.url
        ) {
          // Navigating to the S3 URL (served with an attachment
          // Content-Disposition) triggers the download without leaving the page.
          window.location.href = updatedTask.parameters.url;
          resolve();
        } else {
          reject(new Error("Price report generation failed"));
        }
      },
      onError: (err) => reject(err),
    });
  });
};
